import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from "axios";
import RequestManager from "axios-cancel/lib/RequestManager";
import store from "@ui/stores";

const requestManager = new RequestManager({debug: false});

export type TApiCallConfig = AxiosRequestConfig & {
  useToken?: boolean;
  token?: string;
  baseUrl: string;
  contentType?: string;
  headers?: Record<string, string>;
  cancelPreviousRequests?: boolean;
};

/**
 * Класс для выполнения запросов к API
 */
export default class ApiCaller {
  private readonly callerConfig?: Partial<TApiCallConfig>;

  /**
   * @param {Object} config - Базовый конфиг
   */
  constructor(config?: Partial<TApiCallConfig>) {
    this.callerConfig = config;
  }

	createInstance(
		{
			useToken,
			token,
			baseUrl,
			contentType,
			headers,
			requestId,
			cancelPreviousRequests
		}: Partial<TApiCallConfig>
	): AxiosInstance {
    const instance = axios.create({
      baseURL: baseUrl,
	    withCredentials: false,
      headers: {
        "Content-Type": contentType,
        ...(useToken ? {Authorization: `Bearer ${token}`} : {}),
        ...headers,
      },
      requestId,
    });
    if (requestId) {
      if (cancelPreviousRequests) {
        this.cancelRequest(requestId);
      }
      instance.interceptors.request.use((requestConfig) => {
        if (requestId) {
          const source = axios.CancelToken.source();
          // eslint-disable-next-line no-param-reassign
          requestConfig.cancelToken = source.token;
          requestManager.addRequest(requestId, source.cancel);
        }
        return requestConfig;
      });

      instance.interceptors.response.use((response) => {
        if (requestId) {
          requestManager.removeRequest(requestId);
        }
        return response;
      });
    }
		return  instance;
	}

  /**
   * Выполнение запроса через axios
   *
   * @param {Object} config - Конфиг запроса
   * @returns {*} Данные запроса или ошибка APIError
   */
  async request<T>(config?: Partial<TApiCallConfig>): Promise<T> {
		const {
      useToken = true,
      token = store.user?.token,
      baseUrl = "/pool/v1",
      contentType = "application/json",
      headers = {},
      requestId,
      cancelPreviousRequests,
      ...rest
    } = {
      ...this.callerConfig,
      ...config,
    } as TApiCallConfig;
    let instance = this.createInstance({
	    useToken,
	    token,
	    baseUrl,
	    contentType,
	    headers,
	    requestId,
	    cancelPreviousRequests
		});
    try {
      // для запросов, возвращающих файл возвращается ответ полностью
      // if (rest.responseType === "blob") {
      //   return await instance.request(rest);
      // }
      const {data} = await instance.request<T>(rest);
      return data;
    } catch (error) {
			// @ts-ignore
	    store.debug.error(`url: ${config?.url}, сообщение: ${error.message}`);
      if (axios.isCancel(error)) {
        throw error;
      }
      if (axios.isAxiosError(error)) {
        let axiosError = error;
        const {data: errorData, status} = axiosError.response || ({} as AxiosResponse);
				if (status === 401) {
					await store.user.refresh();
					try {
						instance = this.createInstance({useToken, token: store.user?.token, baseUrl, contentType, headers, requestId, cancelPreviousRequests});
						const {data} = await instance.request<T>(rest);
			      return data;
					} catch (error) {
						// @ts-ignore
						store.debug.error(`url: ${config?.url}, сообщение: ${error.message}`);
						if (axios.isAxiosError(error)) {
							axiosError = error;
						}
					}
				}
        return Promise.reject({...(axiosError.response || {}), message: axiosError.message || "unknown-axios-error"});
      }
      throw error;
    }
  }

  /**
   * Отмена текущего запроса
   */
  cancelRequest = (requestId: string): void => requestManager.cancelRequest(requestId);

  async get<T>(url: string, config?: Partial<TApiCallConfig>): Promise<T> {
    return this.request({...(config || ({} as TApiCallConfig)), url, method: "GET"});
  }

  async post<T>(url: string, data?: any, config?: Partial<TApiCallConfig>): Promise<T> {
    return this.request({...(config || ({} as TApiCallConfig)), url, data, method: "POST"});
  }

  async put<T>(url: string, data?: any, config?: Partial<TApiCallConfig>): Promise<T> {
    return this.request({...(config || ({} as TApiCallConfig)), url, data, method: "PUT"});
  }

  async delete<T>(url: string, config?: Partial<TApiCallConfig>): Promise<T> {
    return this.request({...(config || ({} as TApiCallConfig)), url, method: "DELETE"});
  }

  async patch<T>(url: string, data?: any, config?: Partial<TApiCallConfig>): Promise<T> {
    return this.request({...(config || ({} as TApiCallConfig)), url, data, method: "PATCH"});
  }
}
